import { useId } from "react";
import type { Num } from "../types";

/**
 * Мини-график COT Index юрлиц за последний год (0–100): одна шкала для всех строк, поэтому линии сравнимы между рынками.
 * Под линией — полупрозрачная заливка, крайние зоны 80–100 и 0–20 тонированы.
 */
export function Sparkline({ values, width = 112, height = 32 }: { values: Num[]; width?: number; height?: number }) {
  const gid = useId().replace(/:/g, "");
  const n = values.length;
  if (n < 2) return <svg width={width} height={height} aria-hidden="true" />;
  const x = (i: number) => (i / (n - 1)) * (width - 6) + 1;
  const y = (v: number) => 3 + (1 - v / 100) * (height - 6);
  let d = "";
  let area = "";
  let seg: [number, number][] = [];
  const flush = () => {
    if (seg.length > 1) {
      area += `M${seg[0][0].toFixed(1)},${height}` + seg.map(([a, b]) => `L${a.toFixed(1)},${b.toFixed(1)}`).join("") +
        `L${seg[seg.length - 1][0].toFixed(1)},${height}Z`;
    }
    seg = [];
  };
  values.forEach((v, i) => {
    if (v == null) {
      flush();
      return;
    }
    d += `${seg.length ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    seg.push([x(i), y(v)]);
  });
  flush();
  const last = [...values].reverse().find((v) => v != null) ?? null;
  const tone = last == null ? "" : last >= 80 ? "bull" : last <= 20 ? "bear" : "";
  return (
    <svg width={width} height={height} className={`spark ${tone}`} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="spark-stop-top" />
          <stop offset="1" className="spark-stop-bottom" />
        </linearGradient>
      </defs>
      <rect x={0} y={y(100)} width={width} height={y(80) - y(100)} className="spark-bull" />
      <rect x={0} y={y(20)} width={width} height={y(0) - y(20)} className="spark-bear" />
      <path d={area} fill={`url(#${gid})`} />
      <path d={d} className="spark-line" />
      {last != null && (
        <>
          <circle cx={x(n - 1)} cy={y(last)} r={4.5} className="spark-halo" />
          <circle cx={x(n - 1)} cy={y(last)} r={2.2} className="spark-dot" />
        </>
      )}
    </svg>
  );
}
